import React, { useEffect, useRef, useState } from "react";

import { config } from "../config/index";

type ZendeskChatMessage =
  | { type: "zendesk:jwtRequest" }
  | { type: "zendesk:open" }
  | { type: "zendesk:unreadMessages"; count: number }
  | { type: "zendesk:closed" }
  | { type: "zendesk:pong" };

// CSS injected into the host page (outside the Zendesk iframe)
const hostStyles = `
iframe#webWidget {
  width: 100vw !important;
  max-width: 100vw !important;
  height: 100vh !important;
  max-height: 100vh !important;
}
`;

// CSS injected inside the Zendesk webWidget iframe
const widgetStyles = `
div[data-embed="chat"]{
  padding: 0;
}
div[data-embed="chat"] > div > div:first-child,
header[data-testid="widget-header-view"],
div[data-testid="drop-container"] > div,
div[data-testid="drop-container"] > div > header {
  border-radius: 0;
}
div[data-testid="drop-container"] header h1,
header[data-testid="widget-header-view"] h1 {
  text-align: left;
  margin: auto auto auto 5px !important;
  font-weight: normal;
  color: #B3CEF8;
}
div[data-testid="drop-container"] header h1:before,
header[data-testid="widget-header-view"] h1:before {
  content: "Hi there 👋";
  display: block;
  color: #ffffff;
  font-size: 26px;
  line-height: 32px;
  margin-top: 5px;
}
.coeff__custom_header{
  margin: 5px 0px;
}
.coeff__custom_header .welcome{
  display: block;
  color: #ffffff;
  font-size: 26px;
  line-height: 32px;
  margin-top: 5px;
}
/* Hide minimize button — user closes the window/dialog instead */
button[aria-label*="minimize" i] {
  display: none !important;
}
`;

const postMessageToOpener = (msg: ZendeskChatMessage) => {
  if (window.opener) {
    const targetOrigin = new URLSearchParams(window.location.search).get("opener_origin") ?? "*";
    window.opener.postMessage(JSON.stringify(msg), targetOrigin);
  }
};

const JWT_REQUEST_TIMEOUT_MS = 15_000;
const ZENDESK_SCRIPT_LOAD_TIMEOUT_MS = 30_000;

/** Request a Zendesk JWT from the opener window via postMessage. */
const requestJWTFromOpener = (): Promise<string | undefined> =>
  new Promise(resolve => {
    if (!window.opener) {
      resolve(undefined);
      return;
    }

    const handler = (evt: MessageEvent) => {
      if (evt.source !== window.opener) {
        return;
      }
      try {
        const msg = typeof evt.data === "string" ? JSON.parse(evt.data) : evt.data;
        if (msg.type === "zendesk:jwt") {
          window.removeEventListener("message", handler);
          resolve(msg.token);
        }
      } catch {
        // ignore non-JSON messages
      }
    };
    window.addEventListener("message", handler);
    postMessageToOpener({ type: "zendesk:jwtRequest" });

    setTimeout(() => {
      window.removeEventListener("message", handler);
      resolve(undefined);
    }, JWT_REQUEST_TIMEOUT_MS);
  });

const ZENDESK_HOST_STYLE_ID = "coeff-zendesk-chat-host-style";

const injectCSSToZendeskChat = (zendeskMessengerEnabled: boolean) => {
  if (zendeskMessengerEnabled) {
    return;
  }

  if (!document.getElementById(ZENDESK_HOST_STYLE_ID)) {
    const hostStyle = document.createElement("style");
    hostStyle.id = ZENDESK_HOST_STYLE_ID;
    hostStyle.innerHTML = hostStyles;
    document.head.appendChild(hostStyle);
  }

  const widgetIframe = document.getElementById("webWidget") as HTMLIFrameElement;
  if (widgetIframe) {
    const style = document.createElement("style");
    style.innerHTML = widgetStyles;
    widgetIframe.contentDocument?.head.appendChild(style);
  } else {
    setTimeout(() => injectCSSToZendeskChat(zendeskMessengerEnabled), 500);
  }
};

const LoadingOverlay: React.FC = () => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "white",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "12px",
      fontFamily: "'proxima-nova', sans-serif",
      fontSize: "16px",
      fontWeight: 600,
      color: "rgba(0, 0, 0, 0.85)",
      zIndex: 9999,
    }}
  >
    {/* CSS-only spinner — won't stutter when Zendesk blocks the main thread */}
    <div
      style={{
        width: 32,
        height: 32,
        border: "3px solid #e8e8e8",
        borderTopColor: "#3053FF",
        borderRadius: "50%",
        animation: "zendesk-spin 0.8s linear infinite",
      }}
    />
    <style>{`@keyframes zendesk-spin { to { transform: rotate(360deg); } }`}</style>
    <span>Opening chat...</span>
  </div>
);

const ConnectErrorView: React.FC = () => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "white",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "16px",
      fontFamily: "'proxima-nova', sans-serif",
      fontSize: "16px",
      fontWeight: 600,
      color: "rgba(0, 0, 0, 0.85)",
      zIndex: 9999,
      padding: "24px",
      textAlign: "center",
    }}
  >
    <span>
      Could not connect to Zendesk. Please close this window and open chat again from the app.
    </span>
    <button
      type="button"
      onClick={() => window.close()}
      style={{
        padding: "8px 16px",
        fontSize: "14px",
        fontWeight: 600,
        color: "rgba(0, 0, 0, 0.85)",
        backgroundColor: "#e8e8e8",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
      }}
    >
      Close window
    </button>
  </div>
);

export const ZendeskChat: React.FC = () => {
  const initializedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [connectError, setConnectError] = useState(false);

  useEffect(() => {
    if (initializedRef.current) {
      return;
    }
    initializedRef.current = true;

    // If there's no opener, show same error as connectivity failure
    if (!window.opener) {
      setConnectError(true);
      setLoading(false);
      return;
    }

    const init = async () => {
      const params = new URLSearchParams(window.location.search);
      const messengerEnabled = params.get("messenger_enabled") === "true";

      // Listen for messages from the opener (ping, etc.)
      window.addEventListener("message", (evt: MessageEvent) => {
        if (evt.source !== window.opener) {
          return;
        }
        try {
          const msg = typeof evt.data === "string" ? JSON.parse(evt.data) : evt.data;
          if (msg.type === "zendesk:ping") {
            postMessageToOpener({ type: "zendesk:pong" });
          }
        } catch {
          // ignore non-JSON messages
        }
      });

      window.addEventListener("beforeunload", () => {
        postMessageToOpener({ type: "zendesk:closed" });
      });

      // Request initial JWT from opener to verify connectivity (allows time for zendesk_chat_session_jwt API)
      const initialToken = await requestJWTFromOpener();
      if (!initialToken) {
        setConnectError(true);
        setLoading(false);
        return;
      }

      // Configure zESettings for JWT auth via postMessage
      window.zESettings = {
        webWidget: {
          chat: { suppress: true },
          authenticate: {
            chat: {
              jwtFn: (callback: (token: string) => void) => {
                console.log(`[ZendeskChat] jwtFn called at ${new Date().toISOString()}`);
                requestJWTFromOpener()
                  .then(token => {
                    if (token) {
                      callback(token);
                    }
                  })
                  .catch(error => console.error("[ZendeskChat] jwtFn error:", error));
              },
            },
          },
        },
      };

      // Load Zendesk snippet (reject on error or timeout so init().catch() can show ConnectErrorView)
      await new Promise<void>((resolve, reject) => {
        let settled = false;
        const settle = (fn: () => void) => {
          if (settled) {
            return;
          }
          settled = true;
          fn();
        };

        const script = document.createElement("script");
        script.id = "ze-snippet";
        script.src = `https://static.zdassets.com/ekr/snippet.js?key=${
          messengerEnabled ? config.ZENDESK_MESSENGER_KEY : config.ZENDESK_CHAT_KEY
        }`;
        script.onload = () => {
          clearTimeout(timeoutId);
          settle(resolve);
        };
        script.onerror = () => {
          clearTimeout(timeoutId);
          settle(() => reject(new Error("Zendesk script failed to load")));
        };

        const timeoutId = setTimeout(() => {
          settle(() => reject(new Error("Zendesk script load timed out")));
        }, ZENDESK_SCRIPT_LOAD_TIMEOUT_MS);

        document.head.appendChild(script);
      });

      // Register unread messages listener
      if (messengerEnabled) {
        window.zE("messenger:on", "unreadMessages", (count: number) => {
          postMessageToOpener({ type: "zendesk:unreadMessages", count });
        });
      } else {
        window.zE("webWidget:on", "chat:unreadMessages", (count: number) => {
          postMessageToOpener({ type: "zendesk:unreadMessages", count });
        });
        window.zE("webWidget:on", "chat:connected", () => {
          window.zE("webWidget:on", "chat:unreadMessages", (count: number) => {
            postMessageToOpener({ type: "zendesk:unreadMessages", count });
          });
        });
      }

      // Wait for the widget iframe to actually render before dismissing the overlay
      const waitForWidgetRendered = () =>
        new Promise<void>(resolve => {
          const iframeId = messengerEnabled ? undefined : "webWidget";
          const check = () => {
            const el = iframeId
              ? document.getElementById(iframeId)
              : document.querySelector('iframe[data-product="messenger"]');
            if (el) {
              resolve();
            } else {
              setTimeout(check, 100);
            }
          };
          check();
        });

      // Open widget and dismiss loading overlay once the chat actually renders
      if (messengerEnabled) {
        window.zE("messenger:on", "open", () => {
          postMessageToOpener({ type: "zendesk:open" });
        });
        window.zE("messenger", "open");
        window.zE("messenger", "loginUser");
        await waitForWidgetRendered();
        setLoading(false);
      } else {
        const chatConnectedPromise = new Promise<void>(resolve => {
          window.zE("webWidget:on", "chat:connected", resolve);
        });

        window.zE("webWidget:on", "open", () => {
          postMessageToOpener({ type: "zendesk:open" });
        });
        window.zE("webWidget", "open");
        injectCSSToZendeskChat(messengerEnabled);

        await Promise.all([chatConnectedPromise, waitForWidgetRendered()]);
        setLoading(false);
      }
    };

    init().catch(error => {
      console.error("[ZendeskChat] init error:", error);
      setConnectError(true);
      setLoading(false);
    });
  }, []);

  if (connectError) {
    return <ConnectErrorView />;
  }
  return loading ? <LoadingOverlay /> : null;
};
